import {
	Box,
	Button,
	Drawer,
	Flex,
	Grid,
	Input,
	InputCheckBox,
	RiDownload2Line
} from "@itandi/itandi-bb-ui";
import { SingleComboBox } from "@itandi/itandi-bb-ui/SingleComboBox";
import type { ChangeEvent, ComponentProps } from "react";
import { useCallback, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import DealsCsvExportModal from "~/components/common/dealsCsvExports/DealsCsvExportModal";
import { renderSearchButton } from "~/components/common/parts/search/SearchButton";
import { AdvancedSearchButton } from "~/components/deprecated/AdvancedSearchButton";
import { useDebounce } from "~/hooks/useDebounce";
import { useSessions } from "~/hooks/vendors/useSessions";
import type { DealCompanySetting } from "~/models/CompanySetting";
import { convertToComboBoxOption } from "~/models/CompanySetting";
import type { DealSearchCondition } from "~/models/Deal";
import { searchDetailBadge } from "~/models/Deal";
import { requestCsvGenerate } from "~/utils/api/vendors/CsvGenerateResult";
import Styles from "./DealSearch.module.scss";
import { DealSearchDetailTab } from "./DealSearchDetailTab";

type Props = {
	dealSearchCondition: DealSearchCondition;
	handleChangeCondition: (newCondition: DealSearchCondition) => Promise<void>;
	isSp: boolean;
	maintenanceCompanies: DealCompanySetting[];
};

export function DealSearch({
	dealSearchCondition,
	handleChangeCondition,
	maintenanceCompanies,
	isSp
}: Props) {
	const { data: session } = useSessions().index();
	const methods = useForm<DealSearchCondition>({
		defaultValues: dealSearchCondition,
		mode: "onChange"
	});
	const { reset } = methods;
	const [propertyDealSearchValues, setPropertyDealSearchValues] = useState({
		name: dealSearchCondition.name ?? "",
		roomNumber: dealSearchCondition.roomNumber ?? ""
	});

	const [isOpenSearchDetailTab, setIsOpenSearchDetailTab] = useState(false);
	const selectedCompanyMaster = maintenanceCompanies.find(
		({ companyMasterId }) =>
			companyMasterId === dealSearchCondition.companyMasterId
	);
	const [selectedCompanyMasterOption, setSelectedCompanyMasterOption] =
		useState<ComponentProps<typeof SingleComboBox>["value"]>(
			selectedCompanyMaster
				? convertToComboBoxOption(selectedCompanyMaster)
				: null
		);
	const [isPrioritySort, setIsPrioritySort] = useState(
		dealSearchCondition.prioritySort ?? true
	);
	const handleOpenSearchDetailTab = useCallback((): void => {
		reset(dealSearchCondition);
		setIsOpenSearchDetailTab(true);
	}, [reset, dealSearchCondition]);
	const handleCloseSearchDetailTab = useCallback((): void => {
		setIsOpenSearchDetailTab(false);
	}, []);
	const debounce = useDebounce(200);
	const createOnChange = useCallback(
		(field: keyof Pick<DealSearchCondition, "name" | "roomNumber">) =>
			(e: ChangeEvent<HTMLInputElement>) => {
				const newValue = e.target.value;
				const newCondition: DealSearchCondition = {
					...dealSearchCondition,
					[field]: newValue
				};
				setPropertyDealSearchValues((prev) => ({ ...prev, [field]: newValue }));
				debounce(() => {
					handleChangeCondition(newCondition).then();
				});
			},
		[dealSearchCondition, debounce, handleChangeCondition]
	);
	const onNameChange = createOnChange("name");
	const onRoomNumberChange = createOnChange("roomNumber");
	const onMaintenanceCompanyChange = useCallback(
		async (
			value: ComponentProps<typeof SingleComboBox>["value"]
		): Promise<void> => {
			setSelectedCompanyMasterOption(value);
			const newCondition: DealSearchCondition = {
				...dealSearchCondition,
				companyMasterId: value?.value ? Number(value.value) : undefined
			};
			await handleChangeCondition(newCondition);
		},
		[dealSearchCondition, handleChangeCondition]
	);

	// 簡易検索の入力欄に詳細検索で入力した値を適用する
	const applyConditionToInstantSearch = useCallback(
		(newCondition: DealSearchCondition) => {
			setPropertyDealSearchValues({
				name: newCondition.name ?? "",
				roomNumber: newCondition.roomNumber ?? ""
			});
			const newSelectedCompanyMaster = maintenanceCompanies.find(
				(companyMaster) =>
					companyMaster.companyMasterId === newCondition.companyMasterId
			);
			setSelectedCompanyMasterOption(
				newSelectedCompanyMaster
					? convertToComboBoxOption(newSelectedCompanyMaster)
					: null
			);
			setIsPrioritySort(newCondition.prioritySort ?? true);
		},
		[maintenanceCompanies]
	);

	const handleDetailSearch = useCallback(async (): Promise<void> => {
		const newCondition = methods.getValues();
		// statusId が空配列の場合は「未指定」として扱う
		// 検索後に詳細検索を開いた時にデフォルト選択へ復帰させるため
		if (
			Array.isArray(newCondition.statusId) &&
			newCondition.statusId.length === 0
		) {
			delete newCondition.statusId;
		}
		applyConditionToInstantSearch(newCondition);
		handleCloseSearchDetailTab();
		await handleChangeCondition(newCondition);
	}, [
		methods,
		handleCloseSearchDetailTab,
		handleChangeCondition,
		applyConditionToInstantSearch
	]);

	const [csvExportModalOpen, setCsvExportModalOpen] = useState(false);
	const handleOpenCsvExport = useCallback(() => {
		setCsvExportModalOpen(true);
	}, []);
	const handleCloseCsvExport = useCallback(() => {
		setCsvExportModalOpen(false);
	}, []);
	return (
		<div className={Styles.SimpleSearchContainer}>
			{isSp ? (
				<>
					<Grid gap={8}>
						<Input
							onChange={onNameChange}
							placeholder="物件名"
							type="search"
							value={propertyDealSearchValues.name}
						/>
						<Input
							onChange={onRoomNumberChange}
							placeholder="部屋番号"
							type="search"
							value={propertyDealSearchValues.roomNumber}
						/>
					</Grid>
					<Flex justifyContent="flex-end" marginTop={8}>
						<AdvancedSearchButton
							badge={searchDetailBadge(dealSearchCondition)}
							onClick={handleOpenSearchDetailTab}
						/>
					</Flex>
				</>
			) : (
				<Flex
					alignItems="center"
					justifyContent="space-between"
					marginBottom={8}
				>
					<Box>
						<Grid
							alignItems="center"
							autoFlow="column"
							gap={8}
							justifyContent="start"
						>
							<Box width={240}>
								<Input
									onChange={onNameChange}
									placeholder="物件名"
									type="search"
									value={propertyDealSearchValues.name}
								/>
							</Box>
							<Box width={120}>
								<Input
									onChange={onRoomNumberChange}
									placeholder="部屋番号"
									type="search"
									value={propertyDealSearchValues.roomNumber}
								/>
							</Box>
							<Box width={180}>
								<SingleComboBox
									onChange={onMaintenanceCompanyChange}
									options={maintenanceCompanies.map((cs: DealCompanySetting) =>
										convertToComboBoxOption(cs)
									)}
									placeholder="管理会社"
									value={selectedCompanyMasterOption}
								/>
							</Box>
							<Box>
								<AdvancedSearchButton
									badge={searchDetailBadge(dealSearchCondition)}
									onClick={handleOpenSearchDetailTab}
								/>
							</Box>
							<Box>
								<InputCheckBox
									checked={isPrioritySort}
									label="未読を優先表示する"
									onChange={(e): void => {
										const newChecked = e.target.checked;
										setIsPrioritySort(newChecked);
										const newCondition: DealSearchCondition = {
											...dealSearchCondition,
											prioritySort: newChecked
										};
										handleChangeCondition(newCondition).then();
									}}
								/>
							</Box>
						</Grid>
					</Box>
					{session && !session.admin ? (
						<Box>
							<Button
								onClick={handleOpenCsvExport}
								startIcon={<RiDownload2Line />}
								variant="primary"
							>
								CSV出力
							</Button>
						</Box>
					) : null}
				</Flex>
			)}
			<DealsCsvExportModal
				handleClose={handleCloseCsvExport}
				isOpen={csvExportModalOpen}
				requestCsvGenerate={requestCsvGenerate}
				searchParams={dealSearchCondition}
			/>
			<div className={Styles.SearchDetailContainer}>
				<Drawer
					bottomArea={renderSearchButton(handleDetailSearch)}
					isOpen={isOpenSearchDetailTab}
					onClickBackdrop={handleCloseSearchDetailTab}
					onClickCloseButton={handleCloseSearchDetailTab}
					title="詳細検索"
				>
					<FormProvider
						{...methods}
						key={isOpenSearchDetailTab ? "open" : "close"}
					>
						<DealSearchDetailTab maintenanceCompanies={maintenanceCompanies} />
					</FormProvider>
				</Drawer>
			</div>
		</div>
	);
}
